import { Injectable } from '@angular/core';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PlansStateStructure } from '../configs/interfaces/plansStateStructure.interface';
import { ZipCountyStateStructure } from '../../shared/interfaces/zipCountyStateStructure.interface';
import { EffectiveDateStateStructure } from '../../shared/components/configs/interfaces/effective-date.state-structure.interface';
import { environment } from 'src/environments/environment';
import { PlanDetailsRequestPayload, PlanDetailsMedicareSupplementRequestPayload, PlanDetailsIndividualMarketRequestPayload, PlanDetailsIndividualMarketOnExchangeProductsRequestPayload } from '../services/interfaces/plan-details/plan-details.request.interface';
import { ALL_INDIVIDUAL_PRODUCT_GROUPS, PRODUCT_GROUP_NAMES_INDIVIDUAL, PRODUCT_GROUPS } from '../enums/product-groups.enum';
import { PlanServiceResponse } from '../interfaces/plan-service-response.interface';
import { savePlanDetails, updateLastQueriedIds } from '../configs/reducers/plan-details.reducer';
import { MedicareAdvantage_and_PDP_Product_PlanDetails, MedigapProduct_PlanDetails, Product, IndividualMarketProduct_PlanDetails } from '../interfaces/product.interface';
import { PlanDetailsStateStructure } from '../configs/interfaces/planDetailsStateStructure.interface';
import { PlanAttributes, SummaryView } from '../interfaces/plan-attributes.interface';
import { PlanCollection } from '../interfaces/plan-types-collection.interface';
import { PlanDetailsTransformationUtility } from './plan-details-transformation.utility';
import { PlanDetailsRenderableCategory } from './types/plan-details.types';
import { IndividualPlansService } from '../plan-summary-list/plan-service-strategies/individual-plans.service';
import { IndividualMarketOnExchangeProductsRequestPayload, IndividualMarketProductsRequestPayload } from '../services/interfaces/shared/plan-demographics.interface';
import { PlanUtilityService } from '../plan-summary-list/plan-utility.service';
import { Observable, Subject } from 'rxjs';
import { BrandingConfigurationService, brandNENY, brandWNY } from '@ptl/brand';
@Injectable({
  providedIn: 'root'
})
export class PlanDetailsService {

  drawerChangedSubject = new Subject<void>();
  drawerChanged$: Observable<void> = this.drawerChangedSubject.asObservable();

  constructor(
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private planUtilityService: PlanUtilityService,
    private planDetailsTransformationUtility: PlanDetailsTransformationUtility,
    private individualPlansService: IndividualPlansService,
    private brandService: BrandingConfigurationService  
  ) { }

  /** Uses product-id's identified from view-plans plans to retrieve more detailed product data */
  async getPlanDetails(market: string = 'Senior'):
    Promise<{
      rawData: MedicareAdvantage_and_PDP_Product_PlanDetails[] | MedigapProduct_PlanDetails[],
      transformedData: PlanDetailsRenderableCategory[]
    }> {

    /** Don't xhr the same request */
    // if (this.noChangeToQuery()) {
    //   return this.getTransformedPlanDetailsData();
    // }

    const plansState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const planDetailsState: PlanDetailsStateStructure = this.reducerUtilityService.getState('PlanDetails');

    /**
     * Get Product Ids from Plan Ids
     * ( Storing plan Ids is more unique, though productIds will change each time the flow is uploaded )
     */
    const matchedPlanIds: string[] = [];

    for (const type of Object.keys(plansState.plansByPlanType)) {

      /** Retrieve a collection of plans - as an Object { productId: plan, productId, plan} */
      const products: PlanCollection<Product<PlanAttributes, SummaryView>> = plansState.plansByPlanType[type];

      for (const productId in products) {

        if (planDetailsState.comparablePlanIds.length > 1) {
          /** If compare plans flow */
          /** match plans, add to active plans */
          if (planDetailsState.comparablePlanIds.includes(products[productId].attributes.PLAN_ID)) {
            matchedPlanIds.push(productId);
          }
        } else if (planDetailsState.individualPlanIdForPlanDetails) {
          /** If plan details flow */
          /** Match plan */
          if (planDetailsState.individualPlanIdForPlanDetails.toUpperCase() === products[productId].attributes.PLAN_ID.toUpperCase()) {

            matchedPlanIds.push(productId);

          }
        }
      }
    }

    if (matchedPlanIds.length > 0) {
      await this.getPlansByMatchedIds(matchedPlanIds, market);
      this.ngRedux.dispatch(updateLastQueriedIds(matchedPlanIds));
      return this.getTransformedPlanDetailsData();
    } else {
      return {
        rawData: null,
        transformedData: null
      };
    }
  }

  public async getPlansByMatchedIds(productIds: string[], market: string = 'Senior') {

    /** State Data */
    const plansState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    /** Plan Demographics Data */
    const aspId = environment.initData.brand.ASPID;
    const year = effectiveDateState.effectiveDateYear;
    const county = zipCountyState.county && zipCountyState.county.county;
    const region = zipCountyState.county && zipCountyState.county.region;

    /** Product Id(s) setup */
    const joinedProductIds = productIds.join(',');

    /** Build Non-Medigap Payload */
    const planDetailsPayload: PlanDetailsRequestPayload = {
      aspId, market, county, region, year,
      productId: joinedProductIds
    };

    /** Build MedicareSupplement Payload */
    const planDetailsMSPayload: PlanDetailsMedicareSupplementRequestPayload = {
      aspId, market, county, region, year,
      productId: joinedProductIds
    };

    /** Build Individual Market Payload */
    let planDetailsIndividualPayload: PlanDetailsIndividualMarketRequestPayload = null;

    /** Build Individual Market (ONX) Payload */
    let planDetailsIndividualOnxPayload: PlanDetailsIndividualMarketOnExchangeProductsRequestPayload = null;

    /** Build Medigap Payload (maybe) */
    if (plansState.activeProductGroup === PRODUCT_GROUPS.MEDIGAP) {

      planDetailsPayload.attributes = {
        product_group: PRODUCT_GROUPS.MEDIGAP,
        DOB: plansState.medigapValuesPostFormSubmit.dob,
        coverageStartDate: plansState.medigapValuesPostFormSubmit.coverageDate,
        macra: plansState.medigapValuesPostFormSubmit.macra,
        gender: plansState.medigapValuesPostFormSubmit.gender,
        tobacco: plansState.medigapValuesPostFormSubmit.tobaccoUsage,
        whb: plansState.medigapValuesPostFormSubmit.whb,
        hardStops: plansState.medigapValuesPostFormSubmit && plansState.medigapValuesPostFormSubmit.hardStops || 'true'
      };

      let currentRegion = this.brandService.getCurrentBrand().region;
      if (currentRegion === brandNENY.region || currentRegion === brandWNY.region) {
        delete planDetailsPayload.attributes.tobacco;
        delete planDetailsPayload.attributes.whb;
      }

    } else if (plansState.activeProductGroup.toUpperCase() === PRODUCT_GROUPS.MEDICARE_SUPPLEMENT.toUpperCase()) {

      planDetailsMSPayload.attributes = {
        product_group: PRODUCT_GROUPS.MEDICARE_SUPPLEMENT,
        DOB: plansState.medigapValuesPostFormSubmit.dob,
        coverageStartDate: plansState.medigapValuesPostFormSubmit.coverageDate,
        gender: plansState.medigapValuesPostFormSubmit.gender,
        hardStops: plansState.medigapValuesPostFormSubmit && plansState.medigapValuesPostFormSubmit.hardStops || 'true'
      };

    } else if (ALL_INDIVIDUAL_PRODUCT_GROUPS.includes(plansState.activeProductGroup.toUpperCase())) {

      const individualRequestPayload: IndividualMarketProductsRequestPayload
        = this.individualPlansService.createPayload(plansState.activeProductGroup.toUpperCase() as PRODUCT_GROUP_NAMES_INDIVIDUAL);

      planDetailsIndividualPayload = {
        productId: joinedProductIds,
        applicants: individualRequestPayload.applicants,
        aspId: individualRequestPayload.aspId,
        year: individualRequestPayload.year,
        serviceZone: individualRequestPayload.serviceZone,
        region: individualRequestPayload.region,
        ratingArea: individualRequestPayload.ratingArea,
        market: individualRequestPayload.market,
        effectiveDate: individualRequestPayload.effectiveDate,
        ddToken: individualRequestPayload.ddToken,
        attributes: individualRequestPayload.attributes
      };

    } else if (plansState.activeProductGroup.toUpperCase() === PRODUCT_GROUPS.ON_EXCHANGE.toUpperCase()) {

      const individualOnxRequestPayload
        = this.individualPlansService.createPayload(PRODUCT_GROUPS.ON_EXCHANGE) as IndividualMarketOnExchangeProductsRequestPayload;

      planDetailsIndividualOnxPayload = {
        productId: joinedProductIds,
        applicants: individualOnxRequestPayload.applicants,
        aspId: individualOnxRequestPayload.aspId,
        year: individualOnxRequestPayload.year,
        serviceZone: individualOnxRequestPayload.serviceZone,
        region: individualOnxRequestPayload.region,
        ratingArea: individualOnxRequestPayload.ratingArea,
        market: individualOnxRequestPayload.market,
        effectiveDate: individualOnxRequestPayload.effectiveDate,
        ddToken: individualOnxRequestPayload.ddToken,
        attributes: individualOnxRequestPayload.attributes,
        eligibilityLevel: individualOnxRequestPayload.eligibilityLevel
      };
    }

    /** XHR Request */
    let planDetailsResponse: PlanServiceResponse;
    if (plansState.activeProductGroup.toUpperCase() === PRODUCT_GROUPS.MEDICARE_SUPPLEMENT.toUpperCase()) {
      planDetailsResponse = await this.planUtilityService.getPlansForPlanDetails(planDetailsMSPayload);
    }  
    else if (plansState.activeProductGroup.toUpperCase() === PRODUCT_GROUPS.ON_EXCHANGE.toUpperCase()) {
      planDetailsResponse = await this.planUtilityService.getPlansForPlanDetailsIndividual(planDetailsIndividualOnxPayload);
    } 
    else if (ALL_INDIVIDUAL_PRODUCT_GROUPS.includes(plansState.activeProductGroup.toUpperCase())) {
      planDetailsResponse = await this.planUtilityService.getPlansForPlanDetailsIndividual(planDetailsIndividualPayload);
    }
    else {
      planDetailsResponse = await this.planUtilityService.getPlansForPlanDetails(planDetailsPayload);
    }

    /** Save data to state */
    this.ngRedux.dispatch(savePlanDetails(planDetailsResponse.plans));

    /** Transform data */
    return this.getTransformedPlanDetailsData();
  }

  private async getTransformedPlanDetailsData():
    Promise<{
      rawData: MedicareAdvantage_and_PDP_Product_PlanDetails[] | MedigapProduct_PlanDetails[] | IndividualMarketProduct_PlanDetails[],
      transformedData: PlanDetailsRenderableCategory[]
    }> {

    /** Transform the data for display */
    const untransformedPlans = this.planUtilityService.getPlanDetailsByProductGroupAsArray() as (
      | MedicareAdvantage_and_PDP_Product_PlanDetails[]
      | MedigapProduct_PlanDetails[]
      | IndividualMarketProduct_PlanDetails[]);

    const transformedPlans: PlanDetailsRenderableCategory[] = this.planDetailsTransformationUtility.transformPlans(untransformedPlans);

    return {
      rawData: untransformedPlans,
      transformedData: transformedPlans
    };

  }

  private noChangeToQuery(): boolean {
    const planDetailsState: PlanDetailsStateStructure = this.reducerUtilityService.getState('PlanDetails');

    /** Normalize the arrays as sort, joined strings for comparison */
    const sortedLastQueriedIds = planDetailsState.lastQueriedPlanIds && planDetailsState.lastQueriedPlanIds.sort().join(',');
    const sortedComparableIdsAsString = planDetailsState.comparablePlanIds && planDetailsState.comparablePlanIds.sort().join(',');
    const individualId = planDetailsState.individualPlanIdForPlanDetails;

    switch (sortedLastQueriedIds) {
      case sortedComparableIdsAsString:
      case individualId:
        return true;
      default:
        return false;
    }
  }

  drawerToggled = (): void => {
    this.drawerChangedSubject.next();
  }
}

